import React, { useState, useEffect } from 'react';
import { useMiniERPEDI } from './hooks/useMiniERPEDI';
import { useStatusBanner, StatusBanner } from './components/MiniERPStatusBanner';
import MiniERPTrasContactModal from './MiniERPTrasContactModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone, faEnvelope, faMapMarkerAlt, faMoneyBillWave,
  faFileInvoice, faReceipt, faShareAlt, faLock, faLockOpen, faHistory
} from '@fortawesome/free-solid-svg-icons';

import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

const MiniERPTrasCaseDetail = ({ caseId, onOpenReceipt }) => {
  const {
    fetchTrasCaseDetail, createTrasInteraction, createTrasVisit,
    createTrasCollection, createTrasDocument,
  } = useMiniERPEDI();
  const { status, showSuccess, showError } = useStatusBanner();

  const [detail, setDetail] = useState(null);
  const [contactModal, setContactModal] = useState(null); // 'Call' | 'WhatsApp' | 'Email' | null
  const [collectForm, setCollectForm] = useState({ amount: '', mode: 'Cash', referenceNo: '', remarks: '' });
  const [gpsLoading, setGpsLoading] = useState(false);

  const load = async () => {
    const res = await fetchTrasCaseDetail(caseId);
    setDetail(res?.data);
  };

  useEffect(() => { if (caseId) load(); }, [caseId]);

  const handleContactSave = async (payload) => {
    try {
      await createTrasInteraction(caseId, payload);
      showSuccess(`${payload.channel} logged.`);
      setContactModal(null);
      await load();
    } catch (err) {
      showError(err.message);
    }
  };

  const captureGpsVisit = () => {
    if (!navigator.geolocation) {
      showError('GPS isn\u2019t available on this device/browser.');
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await createTrasVisit(caseId, {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            address: '', // reverse-geocoding provider not wired — coordinates alone are stored
          });
          showSuccess('Visit location captured.');
          await load();
        } catch (err) {
          showError(err.message);
        } finally {
          setGpsLoading(false);
        }
      },
      (err) => {
        showError('Could not get location: ' + err.message);
        setGpsLoading(false);
      }
    );
  };

  const outstanding = detail?.invoice?.outstandingAmount || 0;

  const handleCollectSubmit = async (e) => {
    e.preventDefault();
    const amt = Number(collectForm.amount);
    if (!amt || amt <= 0) {
      showError('Enter a collection amount.');
      return;
    }
    try {
      const res = await createTrasCollection(caseId, { ...collectForm, amount: amt });
      showSuccess(res.message);
      setCollectForm({ amount: '', mode: 'Cash', referenceNo: '', remarks: '' });
      await load();
    } catch (err) {
      showError(err.message);
    }
  };

  const handleDocument = async (docType) => {
    try {
      await createTrasDocument(caseId, docType, outstanding > 0 ? outstanding : detail?.invoice?.amount);
      showSuccess(`${docType} generated.`);
      if (docType === 'Receipt' && onOpenReceipt) onOpenReceipt(detail);
      await load();
    } catch (err) {
      showError(err.message); // this is where the server-side Receipt gate surfaces if hit
    }
  };

  const hasAnyCollection = (detail?.collections || []).length > 0;

  if (!detail) return <div className="p-4 text-gray-500">Loading case...</div>;

  return (
    <div className="p-4">
      {/* Customer header */}
      <div className="bg-gray-800 rounded-2xl p-4 mb-4">
        <div className="text-xs text-gray-500 font-mono">{detail.caseNumber}</div>
        <div className="text-xl font-bold text-white">{detail.invoice?.customerName || 'Walk-in'}</div>
        <div className="text-gray-500 text-sm">{detail.invoice?.customerMobile}</div>
        <div className="flex justify-between items-end mt-3">
          <div>
            <div className="text-gray-500 text-xs">Outstanding</div>
            <div className="text-2xl font-bold text-rose-400">₹{outstanding}</div>
          </div>
          <div className="text-right text-xs text-gray-500">
            Invoice ₹{detail.invoice?.amount} • Due {detail.invoice?.dueDate ? new Date(detail.invoice.dueDate).toLocaleDateString() : '-'}
          </div>
        </div>
      </div>

      <StatusBanner status={status} className="mb-4" />

      {/* Quick Contact */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <button onClick={() => setContactModal('Call')} className="bg-emerald-950/40 border border-emerald-700 rounded-xl py-3 flex flex-col items-center gap-1 text-emerald-300 text-xs">
          <FontAwesomeIcon icon={faPhone} className="text-lg" /> Call
        </button>
        <button onClick={() => setContactModal('WhatsApp')} className="bg-green-950/40 border border-green-700 rounded-xl py-3 flex flex-col items-center gap-1 text-green-300 text-xs">
          <FontAwesomeIcon icon={faWhatsapp} className="text-lg" /> WhatsApp
        </button>
        <button onClick={() => setContactModal('Email')} className="bg-cyan-950/40 border border-cyan-700 rounded-xl py-3 flex flex-col items-center gap-1 text-cyan-300 text-xs">
          <FontAwesomeIcon icon={faEnvelope} className="text-lg" /> Email
        </button>
      </div>

      {/* GPS Visit */}
      <button onClick={captureGpsVisit} disabled={gpsLoading}
        className="w-full bg-gray-800 border border-gray-700 rounded-xl py-3 flex items-center justify-center gap-2 text-sm text-gray-300 mb-4 disabled:opacity-50">
        <FontAwesomeIcon icon={faMapMarkerAlt} /> {gpsLoading ? 'Capturing location...' : 'Log Field Visit (GPS)'}
      </button>

      {/* Record Collection */}
      <div className="bg-gray-800 rounded-2xl p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <FontAwesomeIcon icon={faMoneyBillWave} /> Record Collection
        </h3>
        {outstanding === 0 ? (
          <p className="text-emerald-400 text-sm">✅ Fully collected — nothing outstanding.</p>
        ) : (
          <form onSubmit={handleCollectSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input type="number" placeholder={`Amount (max ₹${outstanding})`} value={collectForm.amount}
                onChange={(e) => setCollectForm({ ...collectForm, amount: e.target.value })}
                className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" required />
              <select value={collectForm.mode} onChange={(e) => setCollectForm({ ...collectForm, mode: e.target.value })}
                className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-sm">
                <option>Cash</option><option>UPI</option><option>Cheque</option><option>NEFT</option>
              </select>
            </div>
            <input type="text" placeholder="Reference No (optional)" value={collectForm.referenceNo}
              onChange={(e) => setCollectForm({ ...collectForm, referenceNo: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" />
            <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 rounded-xl py-2.5 font-medium text-sm">
              Record Collection
            </button>
          </form>
        )}
      </div>

      {/* Documents */}
      <div className="bg-gray-800 rounded-2xl p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Documents &amp; Communication</h3>
        <div className="grid grid-cols-3 gap-2">
          <button onClick={() => handleDocument('Memo')} className="bg-gray-700 hover:bg-gray-600 rounded-xl py-2.5 flex flex-col items-center gap-1 text-xs">
            <FontAwesomeIcon icon={faFileInvoice} /> Memo
          </button>
          <button
            onClick={() => handleDocument('Receipt')}
            disabled={!hasAnyCollection}
            className={`rounded-xl py-2.5 flex flex-col items-center gap-1 text-xs ${hasAnyCollection ? 'bg-cyan-700 hover:bg-cyan-600' : 'bg-gray-900 text-gray-600 cursor-not-allowed'}`}
          >
            <FontAwesomeIcon icon={hasAnyCollection ? faLockOpen : faLock} /> Receipt
          </button>
          <button onClick={() => handleDocument('Share')} className="bg-gray-700 hover:bg-gray-600 rounded-xl py-2.5 flex flex-col items-center gap-1 text-xs">
            <FontAwesomeIcon icon={faShareAlt} /> Share
          </button>
        </div>
        {!hasAnyCollection && <p className="text-gray-500 text-[11px] mt-2">Receipt unlocks once a collection is recorded.</p>}
      </div>

      {/* Settlement Status */}
      <div className="bg-gray-800 rounded-2xl p-4 mb-4 flex justify-between items-center">
        <span className="text-sm text-gray-300">Settlement Status</span>
        <span className={`text-sm font-semibold ${
          detail.status === 'FullyCollected' ? 'text-emerald-400' :
          detail.status === 'PartiallyCollected' ? 'text-amber-400' :
          detail.status === 'Closed' ? 'text-gray-400' : 'text-rose-400'
        }`}>{detail.status}</span>
      </div>

      {/* Journey Timeline */}
      <div className="bg-gray-800 rounded-2xl p-4">
        <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <FontAwesomeIcon icon={faHistory} /> Journey Timeline
        </h3>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {(detail.timeline || []).map((ev, i) => (
            <div key={i} className="flex justify-between text-xs border-b border-gray-700 pb-2">
              <span className="text-gray-300">{ev.label}</span>
              <span className="text-gray-500">{new Date(ev.at).toLocaleDateString()}</span>
            </div>
          ))}
          {(!detail.timeline || detail.timeline.length === 0) && <p className="text-gray-500 text-xs">No activity yet.</p>}
        </div>
      </div>

      {contactModal && (
        <MiniERPTrasContactModal channel={contactModal} onSave={handleContactSave} onClose={() => setContactModal(null)} />
      )}
    </div>
  );
};

export default MiniERPTrasCaseDetail;
